import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { BrandMenu } from "@/components/BrandMenu";
import { Sparkles, Video, PlayCircle, Gift, GraduationCap, Baby, Shield } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6 } }),
};

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const go = () => navigate(user ? "/dashboard" : "/auth");

  return (
    <div className="relative min-h-screen bg-background overflow-hidden">
      {/* ambient glows */}
      <div className="pointer-events-none absolute -top-40 -left-40 w-[40rem] h-[40rem] rounded-full bg-gold/20 blur-[120px]" />
      <div className="pointer-events-none absolute top-1/3 -right-40 w-[38rem] h-[38rem] rounded-full bg-forest/25 blur-[120px]" />

      {/* nav */}
      <header className="sticky top-0 z-40 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <BrandMenu />
          <div className="flex items-center gap-3">
            {user?.role === "admin" && (
              <Button data-testid="nav-admin-btn" variant="outline" onClick={() => navigate("/admin")} className="rounded-full border-gold/40 text-gold bg-transparent hover:bg-gold/10 font-semibold">
                <Shield className="w-4 h-4 mr-1.5" /> Admin
              </Button>
            )}
            <Button data-testid="nav-get-started-btn" onClick={go} className="rounded-full bg-white text-black hover:scale-105 transition-transform font-semibold">
              {user ? "Dashboard" : "Get Started"}
            </Button>
          </div>
        </div>
      </header>

      {/* hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-20 pb-28 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <motion.p variants={fadeUp} initial="hidden" animate="show" className="text-xs tracking-[0.3em] uppercase text-gold font-bold mb-6">
            Live reveal moments, together
          </motion.p>
          <motion.h1 variants={fadeUp} custom={1} initial="hidden" animate="show" className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-none tracking-tighter">
            Press the button.<br />
            <span className="text-gold">Reveal</span> the magic.
          </motion.h1>
          <motion.p variants={fadeUp} custom={2} initial="hidden" animate="show" className="mt-8 text-lg text-foreground/70 max-w-xl leading-relaxed">
            Book a date, invite family and friends to a live video call, and unveil your big news with a jaw-dropping animation. We capture everyone's reactions into a video you'll keep forever.
          </motion.p>
          <motion.div variants={fadeUp} custom={3} initial="hidden" animate="show" className="mt-10 flex flex-wrap gap-4">
            <Button data-testid="hero-cta-btn" onClick={go} className="rounded-full h-14 px-8 text-base bg-gold hover:bg-gold/90 hover:scale-105 transition-transform font-semibold">
              Plan your reveal
            </Button>
            <Button variant="outline" onClick={() => document.getElementById("how").scrollIntoView({ behavior: "smooth" })} className="rounded-full h-14 px-8 text-base border-white/20 bg-transparent hover:bg-white/5">
              How it works
            </Button>
          </motion.div>
        </div>

        <motion.div variants={fadeUp} custom={2} initial="hidden" animate="show" className="lg:col-span-5 relative">
          <div className="animate-float-slow rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
            <img src="https://static.prod-images.emergentagent.com/jobs/ead668e3-67f0-483c-b260-1d54be9b4c44/images/22649e7db69f0686d534a8205aca060a6ac94a672cf56c493f765d5f40fb600e.jpeg" alt="Man on a group video call with family in a conference room" className="w-full h-[26rem] object-cover" />
          </div>
          <div className="absolute -bottom-8 -left-8 w-44 h-44 rounded-2xl overflow-hidden border border-white/10 shadow-2xl hidden sm:block animate-float-slow" style={{ animationDelay: "1.5s" }}>
            <img src="https://images.unsplash.com/photo-1527269534026-c86f4009eace" alt="Celebration confetti" className="w-full h-full object-cover" />
          </div>
        </motion.div>
      </section>

      {/* use cases bento */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <h2 className="font-display font-bold text-3xl sm:text-5xl tracking-tight mb-12">One button. Every big moment.</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Baby, title: "Gender Reveal", desc: "Blue or pink — burst it live with everyone watching.", img: "https://images.unsplash.com/photo-1513151233558-d860c5398176" },
            { icon: GraduationCap, title: "Exam & Results", desc: "Share pass, grades or qualification news the dramatic way.", img: "https://images.unsplash.com/photo-1639765766830-d829d2fe4219" },
            { icon: Gift, title: "Custom Surprise", desc: "Engagements, new jobs, gifts — reveal any secret you like.", img: null },
          ].map((c, i) => (
            <motion.div key={c.title} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="group rounded-2xl bg-card border border-white/5 overflow-hidden hover:-translate-y-1 hover:border-white/20 transition-all duration-300">
              {c.img ? (
                <div className="h-48 overflow-hidden"><img src={c.img} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
              ) : (
                <div className="h-48 flex items-center justify-center bg-gradient-to-br from-forest-dark to-forest/40"><Gift className="w-16 h-16 text-white/80" /></div>
              )}
              <div className="p-7">
                <c.icon className="w-7 h-7 text-gold mb-4" />
                <h3 className="font-display font-bold text-2xl mb-2">{c.title}</h3>
                <p className="text-foreground/60 leading-relaxed">{c.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* how it works */}
      <section id="how" className="relative z-10 max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-3 gap-10">
          {[
            { icon: Sparkles, step: "01", title: "Create & schedule", desc: "Pick your reveal type, set the date and time, add the secret." },
            { icon: Video, step: "02", title: "Invite & gather", desc: "Share a link. Everyone joins the live video call together." },
            { icon: PlayCircle, step: "03", title: "Reveal & relive", desc: "Press the button, the animation fires, reactions get captured." },
          ].map((s, i) => (
            <motion.div key={s.step} variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} className="relative">
              <div className="font-impact text-7xl text-white/10">{s.step}</div>
              <s.icon className="w-8 h-8 text-gold mb-4 -mt-4" />
              <h3 className="font-display font-bold text-2xl mb-2">{s.title}</h3>
              <p className="text-foreground/60 leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 py-24 text-center">
        <h2 className="font-display font-black text-4xl sm:text-6xl tracking-tighter">Your moment deserves a show.</h2>
        <p className="mt-6 text-foreground/60 text-lg max-w-xl mx-auto">Set up your first reveal in under two minutes.</p>
        <Button data-testid="cta-bottom-btn" onClick={go} className="mt-10 rounded-full h-14 px-10 text-base bg-white text-black hover:scale-105 transition-transform font-semibold">
          Start free
        </Button>
      </section>

      <footer className="relative z-10 border-t border-white/10 py-8 text-center text-sm text-foreground/40">
        Unveil — live reveal celebrations. · <button data-testid="footer-contact-link" onClick={() => navigate("/contact")} className="text-gold/80 hover:text-gold transition-colors">Contact</button>
      </footer>
    </div>
  );
}
